import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // SMS WCAG: see #handleFocusIn
    this.addEventListener('focusin', this.#handleFocusIn, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /* SMS WCAG: the suggested search terms are the only true combobox options.
     Product / collection / page / article cards are ordinary links that the
     user reaches with Tab, so they are no longer faked into a listbox. */
  get #suggestionItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.predictive-search-results__wrapper-queries [role="option"]'))
    );
  }

  /* SMS WCAG: every focusable result link outside the suggestion listbox, in
     DOM order, skipping anything hidden from assistive tech or off-screen. */
  get #resultLinks() {
    const { predictiveSearchResults } = this.refs;

    return /** @type {HTMLAnchorElement[]} */ (
      Array.from(predictiveSearchResults.querySelectorAll('a[href]')).filter((link) => {
        if (!(link instanceof HTMLAnchorElement)) return false;
        if (link.closest('.predictive-search-results__wrapper-queries')) return false;
        if (link.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
        return link.offsetParent !== null;
      })
    );
  }

  get #suggestionsListbox() {
    return this.querySelector('.predictive-search-results__wrapper-queries[role="listbox"]');
  }

  get #currentIndex() {
    return this.#suggestionItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const { searchInput } = this.refs;
    const items = this.#suggestionItems;

    let activeItem = null;

    for (const [itemIndex, item] of items.entries()) {
      item.classList.remove('keyboard-focus');

      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        item.classList.add('keyboard-focus');
        activeItem = item;
      } else {
        item.setAttribute('aria-selected', 'false');
      }
    }

    /* SMS WCAG: point the combobox at the highlighted option so screen readers
       announce it while the caret stays in the input (WCAG 4.1.2). */
    if (activeItem) {
      if (!activeItem.id) activeItem.id = `predictive-search-option-${index}`;
      searchInput.setAttribute('aria-activedescendant', activeItem.id);
      activeItem.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      if (document.activeElement !== searchInput) searchInput.focus();
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  get #currentItem() {
    return this.#suggestionItems[this.#currentIndex];
  }

  /* SMS WCAG: drop the highlight without pulling focus back to the input. */
  #clearHighlight() {
    for (const item of this.#suggestionItems) {
      item.classList.remove('keyboard-focus');
      item.setAttribute('aria-selected', 'false');
    }

    this.refs.searchInput.removeAttribute('aria-activedescendant');
  }

  /* SMS WCAG: keep aria-expanded / aria-controls honest after every render. */
  #syncComboboxState() {
    const { searchInput } = this.refs;
    const listbox = this.#suggestionsListbox;

    if (listbox) {
      if (!listbox.id) listbox.id = 'predictive-search-suggestions';
      searchInput.setAttribute('aria-controls', listbox.id);
      searchInput.setAttribute('aria-expanded', 'true');
    } else {
      searchInput.removeAttribute('aria-controls');
      searchInput.setAttribute('aria-expanded', 'false');
    }

    searchInput.removeAttribute('aria-activedescendant');
  }

  /* SMS WCAG: once real focus moves into the results, the input's highlight
     would be a second, competing "current item" — clear it. */
  #handleFocusIn = (event) => {
    const target = event.target;

    if (target instanceof HTMLElement && target !== this.refs.searchInput) {
      this.#clearHighlight();
    }
  };

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    const { searchInput } = this.refs;
    const inputFocused = document.activeElement === searchInput;

    if (event.key === 'Escape') {
      /* SMS WCAG: from inside the results, Escape steps back to the input
         first rather than wiping the query the user just typed. */
      if (!inputFocused) {
        event.preventDefault();
        event.stopPropagation();
        this.#clearHighlight();
        searchInput.focus();
        return;
      }

      this.#resetSearch();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    /* SMS WCAG: Tab is deliberately NOT handled here any more. Horizon used to
       preventDefault() it and move a highlight instead, which left keyboard
       users unable to reach the suggestions, the close button or anything past
       the modal (WCAG 2.1.2 No Keyboard Trap). Tab now does what it always
       does; the arrow keys stay as the fast path. */
    const suggestions = this.#suggestionItems;
    const links = this.#resultLinks;
    const currentIndex = this.#currentIndex;
    const activeLinkIndex = links.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));

    switch (event.key) {
      case 'ArrowDown': {
        if (!suggestions.length && !links.length) return;
        event.preventDefault();

        if (inputFocused) {
          if (suggestions.length && currentIndex < suggestions.length - 1) {
            this.#currentIndex = currentIndex + 1;
          } else if (links.length) {
            this.#clearHighlight();
            links[0]?.focus();
          } else if (suggestions.length) {
            this.#currentIndex = 0;
          }
          return;
        }

        if (activeLinkIndex > -1 && activeLinkIndex < links.length - 1) {
          links[activeLinkIndex + 1]?.focus();
        }
        return;
      }

      case 'ArrowUp': {
        if (!suggestions.length && !links.length) return;
        event.preventDefault();

        if (inputFocused) {
          if (currentIndex > 0) {
            this.#currentIndex = currentIndex - 1;
          } else if (currentIndex === 0) {
            this.#clearHighlight();
          }
          return;
        }

        if (activeLinkIndex > 0) {
          links[activeLinkIndex - 1]?.focus();
          return;
        }

        this.#clearHighlight();
        searchInput.focus();

        if (suggestions.length) this.#currentIndex = suggestions.length - 1;
        return;
      }

      case 'Enter': {
        /* SMS WCAG: when real focus sits on a link or button, let the browser
           activate it natively instead of second-guessing it. */
        if (!inputFocused) return;

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', searchInput.value);
          window.location.href = searchUrl.toString();
        }
        return;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#clearHighlight();

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        // SMS WCAG: aria-expanded / aria-controls must follow the new markup
        this.#syncComboboxState();

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#clearHighlight();
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);

    // SMS WCAG: aria-expanded / aria-controls must follow the new markup
    this.#syncComboboxState();

    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
